import { useCallback } from 'react';

/** The visible rows of one tree, in screen order. */
function rowsIn(container) {
  return Array.from(container?.querySelectorAll('[role="treeitem"][data-node-id]') || []);
}

/**
 * Keys on a focused tree row (roving tabindex — one row is tabbable, arrows
 * move between rows, like the VS Code explorer):
 *
 * - Up / Down: previous / next row; Home / End: first / last row; Right /
 *   Left: expand / collapse (Right on an open row goes to its first child,
 *   Left on a closed row to its parent); Enter: open the page / toggle the folder.
 * - Alt+Up / Alt+Down reorder, Alt+Right nests into the previous sibling,
 *   Alt+Left moves out one level (card 1.7's keyboard alternative to dragging).
 * - Shift+F10 or the context-menu key: the row menu (card 3.3).
 */
export default function useTreeKeyboard({ sectionId, tree, dnd, openRowMenu, setSelected, setFocusId, containerRef }) {
  const { expandedFolders, toggleFolder, childrenByParent } = tree;

  const focusRow = useCallback(
    (el) => {
      if (!el) return;
      const id = el.getAttribute('data-node-id');
      setFocusId(id);
      setSelected({ sectionId, id });
      el.focus({ preventScroll: false });
    },
    [sectionId, setFocusId, setSelected]
  );

  return useCallback(
    (e, node) => {
      if (e.target !== e.currentTarget) return; // a button / the rename field inside the row
      const isOpen = expandedFolders.has(node.id);
      const expandable = node.type === 'folder' || (childrenByParent.get(node.id) || []).length > 0;
      if ((e.shiftKey && e.key === 'F10') || e.key === 'ContextMenu') {
        e.preventDefault();
        openRowMenu(e, node, e.currentTarget);
        return;
      }
      if (e.altKey && e.key.startsWith('Arrow')) {
        if (dnd.moveByKey(node, e.key)) e.preventDefault();
        return;
      }
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      const rows = rowsIn(containerRef.current);
      const at = rows.indexOf(e.currentTarget);
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        focusRow(rows[at + (e.key === 'ArrowDown' ? 1 : -1)]);
      } else if (e.key === 'Home' || e.key === 'End') {
        e.preventDefault();
        focusRow(e.key === 'Home' ? rows[0] : rows[rows.length - 1]);
      } else if (e.key === 'ArrowRight' && expandable && !isOpen) {
        e.preventDefault();
        toggleFolder(node.id);
      } else if (e.key === 'ArrowRight' && expandable && isOpen) {
        // Already open: Right steps into the first child (WAI-ARIA tree pattern).
        e.preventDefault();
        const next = rows[at + 1];
        if (next && Number(next.getAttribute('aria-level')) > Number(e.currentTarget.getAttribute('aria-level'))) focusRow(next);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (expandable && isOpen) toggleFolder(node.id);
        else if (node.parentId != null) focusRow(rows.find((r) => r.getAttribute('data-node-id') === String(node.parentId)));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (node.type === 'folder') toggleFolder(node.id);
        else e.currentTarget.click();
      }
    },
    [expandedFolders, childrenByParent, toggleFolder, dnd, openRowMenu, containerRef, focusRow]
  );
}
